import React, { useEffect, useState } from "react";
import { Alert, Button, Flex, Spin } from "antd";
import { getApiErrorMessage } from "../../../../utils";
import CoursesCard from "./CoursesCard";
import { getDownloadCourses, getDownloadOptions } from "./downloads.service";
import type {
  DownloadCourse,
  DownloadFilters,
  DownloadOptions,
  DownloadParts,
} from "./downloads.types";
import FiltersCard from "./FiltersCard";
import RunSummary from "./RunSummary";
import { useDownloadRun } from "./useDownloadRun";

const NO_FILTERS: DownloadFilters = {
  grades: [],
  subjects: [],
  trimesters: [],
};
const ALL_PARTS: DownloadParts = {
  od: true,
  quiz: true,
  quizVideo: true,
  odSummary: false,
};

type OptionsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; options: DownloadOptions };

const Descargas: React.FC = () => {
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<OptionsState>({ status: "loading" });
  const [filters, setFilters] = useState(NO_FILTERS);
  const [parts, setParts] = useState(ALL_PARTS);
  // Null until a list is asked for, and again after Nueva descarga.
  const [courses, setCourses] = useState<DownloadCourse[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [listing, setListing] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const run = useDownloadRun();

  useEffect(() => {
    let ignore = false;
    setLoaded({ status: "loading" });
    getDownloadOptions().then(
      (options) => {
        if (!ignore) setLoaded({ status: "ready", options });
      },
      (err) => {
        if (ignore) return;
        setLoaded({
          status: "error",
          message: getApiErrorMessage(
            err,
            "No se pudieron cargar las opciones de descarga.",
          ),
        });
      },
    );
    return () => {
      ignore = true;
    };
  }, [attempt]);

  if (loaded.status === "loading") {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }
  if (loaded.status === "error") {
    return (
      <Alert
        type="error"
        showIcon
        style={{ whiteSpace: "pre-line" }}
        message={loaded.message}
        action={
          <Button size="small" onClick={() => setAttempt((n) => n + 1)}>
            Reintentar
          </Button>
        }
      />
    );
  }

  const { options } = loaded;
  const shown =
    run.phase === "running" || run.phase === "finished" ? run.queue : courses;
  const error = run.error ?? listError;
  // Frozen only while something is under way: once a run is over, listing
  // again starts afresh.
  const busy = listing || run.phase === "starting" || run.phase === "running";

  const destinationOf = (of: DownloadParts) =>
    of.odSummary
      ? {
          folderUrl: options.odSummaryFolderUrl ?? null,
          trackerUrl: options.odSummaryTrackerUrl ?? null,
        }
      : { folderUrl: options.folderUrl, trackerUrl: options.trackerUrl };

  const handleList = async () => {
    setListing(true);
    setListError(null);
    run.reset();
    try {
      const response = await getDownloadCourses(filters);
      setCourses(response.courses);
      setSelectedIds(response.courses.map((course) => course.courseId));
    } catch (err) {
      setCourses(null);
      setListError(
        getApiErrorMessage(err, "No se pudieron listar los cursos."),
      );
    } finally {
      setListing(false);
    }
  };

  const handleDownload = () => {
    const marked = new Set(selectedIds);
    void run.start(
      (courses ?? []).filter((course) => marked.has(course.courseId)),
      parts,
    );
  };

  const handleNewDownload = () => {
    run.reset();
    setCourses(null);
    setSelectedIds([]);
  };

  return (
    <Flex vertical gap={24}>
      <FiltersCard
        options={options}
        value={filters}
        parts={parts}
        disabled={busy}
        listing={listing}
        onChange={setFilters}
        onPartsChange={setParts}
        onList={handleList}
      />
      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ whiteSpace: "pre-line" }}
          message={error}
        />
      ) : null}
      {shown ? (
        <CoursesCard
          courses={shown}
          rows={run.rows}
          parts={parts}
          selectedIds={selectedIds}
          phase={run.phase}
          {...destinationOf(parts)}
          onSelect={setSelectedIds}
          onDownload={handleDownload}
        />
      ) : null}
      {run.phase === "finished" ? (
        <RunSummary
          states={Object.values(run.rows)}
          {...destinationOf(run.runParts ?? parts)}
          onNewDownload={handleNewDownload}
        />
      ) : null}
    </Flex>
  );
};

export default Descargas;
